// src/services/actividadService.js
//
// Registro de actividad para el módulo "Historial de Actividad" (auditoría).
//
// FIRE-AND-FORGET: llamar a registrarActividad(...) NUNCA bloquea ni rompe la
// acción del usuario. Si falla (sin red, CF caída, sin sesión), se ignora en
// silencio — un log de auditoría es best-effort, jamás debe frenar una venta.
//
// La identidad (QUIÉN hizo la acción) la pone el SERVIDOR desde el token; acá
// NO se manda el email. Por eso da igual si el componente que llama no tiene el
// usuario a mano: basta con haber iniciado sesión.
//
//   registrarActividad('pedidos', 'crear_pedido', 'Creó el pedido #12345', { pedidoId, numero });

import { auth } from './firebase';

const FUNCTIONS_URL = 'https://us-central1-app-newparalellum.cloudfunctions.net';

/**
 * @param {string} modulo       dominio de la acción (ej. 'pedidos', 'sesion')
 * @param {string} accion       clave corta (ej. 'crear_pedido', 'login')
 * @param {string} [descripcion] texto legible ("Creó el pedido #12345")
 * @param {object} [detalle]    objeto plano opcional con IDs/metadatos
 */
export async function registrarActividad(modulo, accion, descripcion = '', detalle = null) {
  try {
    const usuario = auth.currentUser;
    if (!usuario) return; // sin sesión no hay nada que registrar
    const token = await usuario.getIdToken();

    // No await del fetch: fire-and-forget. `keepalive` deja que el request
    // sobreviva a una navegación o cierre de sesión (importante en logout).
    fetch(`${FUNCTIONS_URL}/registrarActividad`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ modulo, accion, descripcion, detalle }),
      keepalive: true,
    }).catch(() => {});
  } catch (e) {
    // best-effort: jamás propagar el error a la acción real
  }
}

/**
 * Consulta la actividad del ÁREA del solicitante (pestaña "Mi área" del módulo
 * Historial de Actividad). La pertenencia al área (departamentos_empresa donde
 * el usuario es responsable) la resuelve la CF en el servidor — el navegador no
 * lee `registro_actividad` directo para esta vista (ver firestore.rules).
 * A diferencia de registrarActividad, esta SÍ lanza si falla: la pantalla
 * necesita saber si la búsqueda salió bien.
 *
 * @param {string} desde   'YYYY-MM-DD'
 * @param {string} hasta   'YYYY-MM-DD'
 * @param {string} [usuarioEmail] filtrar a un colaborador puntual del área
 * @returns {Promise<{success: true, registros: Array, sinArea?: boolean, acotado?: boolean}>}
 */
export async function consultarHistorialArea(desde, hasta, usuarioEmail = '') {
  const usuario = auth.currentUser;
  if (!usuario) throw new Error('Sesión vencida. Volvé a iniciar sesión.');
  const token = await usuario.getIdToken();
  const resp = await fetch(`${FUNCTIONS_URL}/consultarHistorialArea`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ desde, hasta, usuarioEmail: usuarioEmail || null }),
  });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok || !data.success) {
    throw new Error(data.error || 'No se pudo consultar el historial del área');
  }
  return data;
}

export default registrarActividad;
